import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AjaxService } from '../../../service/ajax.service';
import { UtilService } from '../../../service/util.service';
import { API } from '../../../../environments/environment';
import { UserService } from 'src/app/services/user.service';

interface Horario {
  id: number;
  idusuario: number;
  inicio: string;
  fim: string;
  intervalominutos: number;
  idencrypted: string;
  idtipodiasemana: number | null;
  inicioperiodo: string | null;
  fimperiodo: string | null;
  idempresacompartilhamento: number | null;
  datacadastro: string;
  dataformatadasemhora: string;
  idgrupo: string;
  presencialonline: string;
  tipopresencialonline: string;
  datafimformatada: string;
  horaformatada: string;
  horafimformatada: string;
  nomeempresacompartilhamento: string | null;
  isbloqueado: boolean;
  bloqueado: string;
}

interface AssessorOption {
  id: number;
  idUsuario: number;
  nome: string;
  email: string;
  idUsuarioEncript: string;
}

@Component({
  selector: 'app-disponibilizar-horarios',
  templateUrl: './disponibilizar-horarios.component.html',
  styleUrls: ['./disponibilizar-horarios.component.scss']
})
export class DisponibilizarHorariosComponent implements OnInit {

  // Dados do assessor selecionado
  assessorSelecionado: any = null;
  assessoresDisponiveis: AssessorOption[] = [];
  assessorSelecionadoId: string = '';

  // Loading e erro
  loading: boolean = false;
  errorMessage: string = '';

  // Formulário
  formData = {
    intervalo: 30,
    data: '',
    horaInicio: '09:00',
    horaFim: '17:00',
    tipo: 'online',
    bloquearHorario: false
  };

  // Lista de horários cadastrados
  horarios: Horario[] = [];
  horariosFiltrados: Horario[] = [];
  
  // Paginação
  currentPage: number = 1;
  itemsPerPage: number = 10;
  totalItems: number = 0;
  totalPages: number = 0;
  horariosPaginados: Horario[] = [];
  
  // Math para uso no template
  Math = Math;

  constructor(
    private route: ActivatedRoute,
    private ajaxService: AjaxService,
    private util: UtilService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    this.preencherDataAtual();
    this.carregarDadosAssessor();
  }

  // Preencher campo data com a data atual
  preencherDataAtual(): void {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');
    this.formData.data = `${ano}-${mes}-${dia}`;
  }

  // Carregar dados do assessor selecionado
  carregarDadosAssessor(): void {
    this.route.queryParams.subscribe(params => {
      const idUsuarioEnc = params['idUsuarioEncript'] || params['idUsuario'] || '';
      const assessorId = params['assessorId'] || '';

      if (idUsuarioEnc || assessorId) {
        this.assessorSelecionado = {
          id: Number(assessorId || 0),
          nome: params['assessorNome'] || params['nomeUsuario'] || 'Assessor',
          email: params['assessorEmail'] || '',
          idUsuarioEncript: idUsuarioEnc
        };
        console.log('Assessor selecionado para agendamento:', this.assessorSelecionado);
        this.carregarHorarios();
        return;
      }

      this.carregarAssessores();
    });
  }

  carregarAssessores(): void {
    const url = API + 'empresa/UsuarioFisioterapeuta/ConsultarUsuarios';
    this.util.showLoading();

    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.util.closeLoading();
        const lista = response?.data && Array.isArray(response.data) ? response.data : [];
        this.assessoresDisponiveis = lista
          .filter((item: any) => item && typeof item === 'object')
          .map((item: any) => this.util.converterParaLowercase(item))
          .map((item: any) => ({
            id: Number(item.id || 0),
            idUsuario: Number(item.idusuario || 0),
            nome: item.nome || 'Assessor',
            email: item.email || '',
            idUsuarioEncript: item.idusuarioencript || ''
          }))
          .filter((item: AssessorOption) => !!item.idUsuarioEncript);

        this.preSelecionarAssessorLogado();
      },
      error: () => {
        this.util.closeLoading();
        this.assessoresDisponiveis = [];
        this.util.toasterShowError('Erro', 'Não foi possível carregar a lista de assessores.');
      }
    });
  }

  selecionarAssessor(): void {
    const idSelecionado = this.assessorSelecionadoId;
    if (!idSelecionado) {
      this.util.toasterShowError('Atenção', 'Selecione um assessor para continuar.');
      return;
    }

    const assessor = this.assessoresDisponiveis.find((a) => a.idUsuarioEncript === idSelecionado);
    if (!assessor) {
      this.util.toasterShowError('Erro', 'Assessor selecionado não encontrado.');
      return;
    }

    this.assessorSelecionado = {
      id: assessor.id,
      nome: assessor.nome,
      email: assessor.email,
      idUsuarioEncript: assessor.idUsuarioEncript
    };

    this.carregarHorarios();
  }

  private preSelecionarAssessorLogado(): void {
    const idUsuarioLogado = this.getIdUsuarioLogado();
    if (!idUsuarioLogado || this.assessoresDisponiveis.length === 0) return;

    const assessorLogado = this.assessoresDisponiveis.find((a) => a.idUsuario === idUsuarioLogado);
    if (!assessorLogado) return;

    this.assessorSelecionadoId = assessorLogado.idUsuarioEncript;
    this.assessorSelecionado = {
      id: assessorLogado.id,
      nome: assessorLogado.nome,
      email: assessorLogado.email,
      idUsuarioEncript: assessorLogado.idUsuarioEncript
    };

    this.carregarHorarios();
  }

  private getIdUsuarioLogado(): number | null {
    const userData = this.userService.getUserData();
    if (!userData) return null;

    const userId =
      Number(userData.User?.Id) ||
      Number((userData as any)?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/sid']) ||
      0;

    return userId > 0 ? userId : null;
  }

  // Carregar dados dos horários via API
  carregarHorarios(): void {
    if (!this.assessorSelecionado?.idUsuarioEncript) {
      console.log('ID do usuário encriptado não disponível');
      this.horarios = [];
      this.horariosFiltrados = [];
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    
    const url = API + `empresa/UsuarioHorarioDisponivel/ConsultarUsuarioHorarioDisponivel?idUsuario=${this.assessorSelecionado.idUsuarioEncript}`;
    
    this.util.showLoading();
    
    this.ajaxService.get(url).subscribe({
      next: (response) => {
        this.util.closeLoading();
        this.loading = false;
        console.log('Resposta da API de horários:', response);
        
        if (response && response.data && Array.isArray(response.data)) {
          // Filtrar apenas horários válidos e converter para lowercase
          this.horarios = response.data
            .filter(horario => horario && typeof horario === 'object')
            .map(horario => this.util.converterParaLowercase(horario))
            .map(horario => {
              const isBloqueado = this.parseBoolean(
                horario.isbloqueado !== undefined ? horario.isbloqueado : horario.is_bloqueado
              );
              const presencialOnline = this.normalizarPresencialOnline(
                horario.presencialonline || horario.presencial_online
              );
              const tipoPresencialOnline = this.resolverTipoPresencialOnline(
                presencialOnline,
                horario.tipopresencialonline || horario.tipo_presencial_online
              );

              return {
                id: horario.id || 0,
                idusuario: horario.idusuario || 0,
                inicio: horario.inicio || '',
                fim: horario.fim || '',
                intervalominutos: horario.intervalominutos || 30,
                idencrypted: horario.idencrypted || '',
                idtipodiasemana: horario.idtipodiasemana || null,
                inicioperiodo: horario.inicioperiodo || null,
                fimperiodo: horario.fimperiodo || null,
                idempresacompartilhamento: horario.idempresacompartilhamento || null,
                datacadastro: horario.datacadastro || '',
                dataformatadasemhora: horario.dataformatadasemhora || '',
                idgrupo: horario.idgrupo || '',
                presencialonline: presencialOnline,
                tipopresencialonline: tipoPresencialOnline,
                datafimformatada: horario.datafimformatada || '',
                horaformatada: horario.horaformatada || '',
                horafimformatada: horario.horafimformatada || '',
                nomeempresacompartilhamento: horario.nomeempresacompartilhamento || null,
                isbloqueado: isBloqueado,
                bloqueado: isBloqueado ? 'Bloqueado' : 'Disponível'
              };
            });
          
          console.log('Horários mapeados:', this.horarios);
        } else {
          console.log('Resposta inválida ou sem dados:', response);
          this.horarios = [];
        }
        
        this.horariosFiltrados = [...this.horarios];
        this.atualizarPaginacao();
      },
      error: (error) => {
        this.util.closeLoading();
        this.loading = false;
        console.error('Erro ao carregar horários:', error);
        this.util.toasterShowError('Erro', 'Erro ao carregar horários disponíveis');
        this.horarios = [];
        this.horariosFiltrados = [];
      }
    });
  }

  // Adicionar novo horário
  adicionarHorario(): void {
    if (!this.formData.data || !this.formData.horaInicio || !this.formData.horaFim) {
      alert('Por favor, preencha a data e os horários de início e fim.');
      return;
    }

    if (!this.assessorSelecionado?.idUsuarioEncript) {
      alert('ID do usuário não disponível.');
      return;
    }

    // Formatar horários para o formato HH:mm:ss
    const horaInicioFormatada = this.formData.horaInicio + ':00';
    const horaFimFormatada = this.formData.horaFim + ':00';

    // Montar objeto para a API
    const horarioParaAPI = {
      intervaloEmMinutos: this.formData.intervalo,
      data: this.formData.data,
      hora: horaInicioFormatada,
      horaFim: horaFimFormatada,
      dataFim: this.formData.data, // Usar a mesma data para início e fim
      PRESENCIAL_ONLINE: this.formData.tipo === 'presencial' ? 'P' : 'O',
      isBloqueado: this.formData.bloquearHorario,
      idUsuarioEnc: this.assessorSelecionado.idUsuarioEncript
    };

    console.log('Enviando horário para API:', horarioParaAPI);

    this.util.showLoading();

    const url = API + 'empresa/UsuarioHorarioDisponivel/SaveAsync';
    
    this.ajaxService.post(url, horarioParaAPI).subscribe({
      next: (response) => {
        this.util.closeLoading();
        console.log('Resposta da API ao salvar horário:', response);
        
        // Recarregar horários após salvar
        this.carregarHorarios();
        
        // Limpar formulário
        this.limparFormulario();
        
        this.util.toasterShowSuccess('Sucesso', 'Horário adicionado com sucesso!');
      },
      error: (error) => {
        this.util.closeLoading();
        console.error('Erro ao salvar horário:', error);
        this.util.toasterShowError('Erro', 'Erro ao salvar horário. Tente novamente.');
      }
    });
  }

  // Limpar formulário
  limparFormulario(): void {
    this.formData = {
      intervalo: 30,
      data: '',
      horaInicio: '09:00',
      horaFim: '17:00',
      tipo: 'online',
      bloquearHorario: false
    };
  }

  // Editar horário
  editarHorario(horario: Horario): void {
    console.log('Editar horário:', horario);
    alert(`Editar horário: ${horario.dataformatadasemhora} - ${horario.horaformatada} às ${horario.horafimformatada}`);
  }

  // Excluir horário
  excluirHorario(horario: Horario): void {
    console.log('Excluir horário:', horario);
    if (confirm(`Tem certeza que deseja excluir o horário "${horario.dataformatadasemhora} - ${horario.horaformatada} às ${horario.horafimformatada}"?`)) {
      const idParam = encodeURIComponent(horario.idencrypted || String(horario.id || ''));
      const url = API + `empresa/UsuarioHorarioDisponivel/DeleteAsync?id=${idParam}`;

      this.util.showLoading();
      this.ajaxService.post(url, {}).subscribe({
        next: (response) => {
          this.util.closeLoading();

          if (response?.hasErrors) {
            this.util.toasterShowError('Erro', response?.message || 'Não foi possível excluir o horário.');
            return;
          }

          this.util.toasterShowSuccess('Sucesso', 'Horário excluído com sucesso!');
          this.carregarHorarios();
        },
        error: () => {
          this.util.closeLoading();
          this.util.toasterShowError('Erro', 'Erro ao excluir horário. Tente novamente.');
        }
      });
    }
  }

  // Alterar status de bloqueio
  alterarBloqueio(horario: Horario): void {
    horario.isbloqueado = !horario.isbloqueado;
    horario.bloqueado = horario.isbloqueado ? 'Sim' : 'Não';
    console.log(`Status de bloqueio alterado para: ${horario.isbloqueado ? 'Bloqueado' : 'Desbloqueado'}`);
    alert(`Horário ${horario.isbloqueado ? 'bloqueado' : 'desbloqueado'} com sucesso!`);
  }

  // Obter classe CSS para tipo
  getTipoClass(tipo: string): string {
    switch (this.toText(tipo).toLowerCase()) {
      case 'p':
      case 'presencial': return 'badge bg-primary';
      case 'o':
      case 'online': return 'badge bg-success';
      default: return 'badge bg-light text-dark';
    }
  }

  // Obter classe CSS para status de bloqueio
  getBloqueioClass(isbloqueado: boolean): string {
    return isbloqueado ? 'badge bg-danger' : 'badge bg-success';
  }

  // Obter texto para status de bloqueio
  getBloqueioText(isbloqueado: boolean): string {
    return isbloqueado ? 'Bloqueado' : 'Disponível';
  }

  private parseBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;

    const text = String(value || '').trim().toLowerCase();
    return text === '1' || text === 'true' || text === 'sim' || text === 'bloqueado';
  }

  private normalizarPresencialOnline(value: any): string {
    const tipo = this.toText(value).toUpperCase();
    if (tipo === 'P' || tipo === 'PRESENCIAL') return 'P';
    if (tipo === 'O' || tipo === 'ONLINE') return 'O';
    return '';
  }

  private resolverTipoPresencialOnline(sigla: string, label: any): string {
    const tipoSigla = this.normalizarPresencialOnline(sigla);
    if (tipoSigla === 'P') return 'Presencial';
    if (tipoSigla === 'O') return 'Online';

    const textoLabel = this.toText(label);
    if (!textoLabel) return 'Não informado';
    return textoLabel;
  }

  private toText(value: any): string {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  // Métodos de paginação
  atualizarPaginacao(): void {
    this.totalItems = this.horariosFiltrados.length;
    this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
    
    // Garantir que a página atual não exceda o total de páginas
    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = this.totalPages;
    }
    
    // Calcular índices para paginação
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    
    // Obter itens da página atual
    this.horariosPaginados = this.horariosFiltrados.slice(startIndex, endIndex);
  }

  // Ir para página específica
  irParaPagina(pagina: number): void {
    if (pagina >= 1 && pagina <= this.totalPages) {
      this.currentPage = pagina;
      this.atualizarPaginacao();
    }
  }

  // Página anterior
  paginaAnterior(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.atualizarPaginacao();
    }
  }

  // Próxima página
  proximaPagina(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.atualizarPaginacao();
    }
  }

  // Primeira página
  primeiraPagina(): void {
    this.currentPage = 1;
    this.atualizarPaginacao();
  }

  // Última página
  ultimaPagina(): void {
    this.currentPage = this.totalPages;
    this.atualizarPaginacao();
  }

  // Alterar itens por página
  alterarItensPorPagina(): void {
    this.currentPage = 1; // Voltar para primeira página
    this.atualizarPaginacao();
  }

  // Obter array de páginas para exibição
  getPaginasParaExibicao(): number[] {
    const paginas: number[] = [];
    const maxPaginasVisiveis = 5;
    
    let inicio = Math.max(1, this.currentPage - Math.floor(maxPaginasVisiveis / 2));
    let fim = Math.min(this.totalPages, inicio + maxPaginasVisiveis - 1);
    
    // Ajustar início se estivermos no final
    if (fim - inicio + 1 < maxPaginasVisiveis) {
      inicio = Math.max(1, fim - maxPaginasVisiveis + 1);
    }
    
    for (let i = inicio; i <= fim; i++) {
      paginas.push(i);
    }
    
    return paginas;
  }
} 